import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCreateItemMutation, useGetContentItemsQuery } from '../store/docApi';
import { Button, Box, Typography, TextField, Switch } from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import NewItemDialog from '../components/widgets/NewItemDialog';
import WorkspaceCard from './WorkspaceCard';

export default function WorkspacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams(); // Hook to manage URL parameters
  const { data: items, error, isLoading } = useGetContentItemsQuery({ label: 'workspace' });
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isExpandedView, setIsExpandedView] = useState(() => searchParams.get('view') === 'expanded'); // Initialize from URL
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createItem, result] = useCreateItemMutation();

  useEffect(() => {
    if (items && Array.isArray(items)) {
      // Sort items by _ts field in descending order (most recent first)
      const sortedItems = [...items].sort((a, b) => (b._ts || 0) - (a._ts || 0));
      setFilteredItems(sortedItems);
    } else {
      setFilteredItems([]);
    }
  }, [items]);

  useEffect(() => {
    if (!items || !Array.isArray(items)) {
      setFilteredItems([]);
      return;
    }

    if (searchKeyword) {
      const filtered = items.filter(
        (item) => item.motifs && item.motifs.some((motif) => (motif.keywords || '').toLowerCase().includes(searchKeyword.toLowerCase()))
      );
      // Sort filtered items by _ts field in descending order
      const sortedFiltered = [...filtered].sort((a, b) => (b._ts || 0) - (a._ts || 0));
      setFilteredItems(sortedFiltered);
    } else {
      // Sort all items by _ts field in descending order
      const sortedItems = [...items].sort((a, b) => (b._ts || 0) - (a._ts || 0));
      setFilteredItems(sortedItems);
    }
  }, [searchKeyword, items]);

  // Update the URL parameter whenever isExpandedView changes
  const toggleExpandedView = () => {
    const newView = !isExpandedView;
    setIsExpandedView(newView);
    if (newView) {
      setSearchParams({ view: 'expanded' }); // Add `view=expanded` to the URL
    } else {
      setSearchParams({}); // Remove the parameter for the default view
    }
  };

  async function handleCreateWorkspace(id, name) {
    setIsCreateDialogOpen(false);
    const newWorkspace = {
      label: 'workspace',
      id: id,
      name: name,
      motifs: {},
      colorways: {},
      patterns: {},
      parts: {},
      layouts: {}
    };
    try {
      const response = await createItem({ label: 'workspace', data: newWorkspace }).unwrap();
      navigate(`/workspaces/${response.pk}/${response.id}`);
    } catch (error) {
      console.error('Error creating workspace:', error);
    }
  }

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading content</Typography>;

  return (
    <Box>
      {result && result.isError && (
        <Typography color="error" sx={{ mb: 2 }}>
          Error creating a new workspace.
        </Typography>
      )}

      {filteredItems.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            gap: 2
          }}
        >
          <Typography variant="h6" color="text.secondary" align="center">
            No workspaces found
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setIsCreateDialogOpen(true)}>
            Create your first workspace
          </Button>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              gap: 2
            }}
          >
            <TextField
              size="small"
              placeholder="Search workspaces..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1 }} />
              }}
            />
            <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setIsCreateDialogOpen(true)}>
              Create
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography component="label" sx={{ mr: 1 }}>
              Show motifs
            </Typography>
            <Switch checked={isExpandedView} onChange={toggleExpandedView} color="primary" />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              justifyContent: 'center'
            }}
          >
            {filteredItems.map((item, i) => (
              <WorkspaceCard key={i} item={item} isExpandedView={isExpandedView} searchKeyword={searchKeyword} navigate={navigate} />
            ))}
          </Box>
        </>
      )}
      <NewItemDialog
        title="Create Workspace"
        onSave={handleCreateWorkspace}
        onClose={() => setIsCreateDialogOpen(false)}
        open={isCreateDialogOpen}
      />
    </Box>
  );
}
